import { initializeApp, getApps, getApp } from 'firebase/app';
import { getAuth, signInAnonymously, setPersistence, browserLocalPersistence, onAuthStateChanged } from 'firebase/auth';
import { 
  getFirestore,
  collection,
  query,
  where,
  getDocs,
  orderBy,
  onSnapshot,
  addDoc,
  serverTimestamp,
  Timestamp,
  doc,
  getDoc
} from '@firebase/firestore';
import { getStorage } from 'firebase/storage';
import {
  REACT_APP_FIREBASE_API_KEY,
  REACT_APP_FIREBASE_AUTH_DOMAIN,
  REACT_APP_FIREBASE_PROJECT_ID,
  REACT_APP_FIREBASE_STORAGE_BUCKET,
  REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  REACT_APP_FIREBASE_APP_ID,
  REACT_APP_FIREBASE_MEASUREMENT_ID
} from '@env';
import AsyncStorage from '@react-native-async-storage/async-storage';

// Firebase configuration
const firebaseConfig = {
  apiKey: REACT_APP_FIREBASE_API_KEY,
  authDomain: REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: REACT_APP_FIREBASE_APP_ID,
  measurementId: REACT_APP_FIREBASE_MEASUREMENT_ID
};

// Initialize Firebase only if it hasn't been initialized yet
const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApp();

// Initialize Auth and Firestore
const auth = getAuth(app);

// Set persistence to LOCAL
setPersistence(auth, browserLocalPersistence).catch((error) => {
  console.error("Auth persistence error:", error);
});

// Add auth state listener
onAuthStateChanged(auth, async (user) => {
  if (user) {
    // User is signed in
    const isLoggedInStorage = await AsyncStorage.getItem('isLoggedIn');
    const groupId = await AsyncStorage.getItem('currentGroupId');
    const qrCode = await AsyncStorage.getItem('currentQrCode');

    if (!isLoggedInStorage || !groupId || !qrCode) {
      // If storage data is missing, verify group access
      const groupRef = doc(firestore, 'groups', groupId || '');
      try {
        const groupDoc = await getDoc(groupRef);
        if (groupDoc.exists() && groupDoc.data().qrCode === qrCode) {
          await AsyncStorage.setItem('isLoggedIn', 'true');
          await AsyncStorage.setItem('currentGroupId', groupId || '');
          await AsyncStorage.setItem('currentQrCode', qrCode || '');
        } else {
          // Invalid group or QR code, sign out
          await auth.signOut();
          await AsyncStorage.multiRemove(['isLoggedIn', 'currentGroupId', 'currentQrCode']);
        }
      } catch (error) {
        console.error('Error verifying group access:', error);
      }
    }
  } else {
    // User is signed out
    await AsyncStorage.multiRemove(['isLoggedIn', 'currentGroupId', 'currentQrCode']);
  }
});

const firestore = getFirestore(app);
const storage = getStorage(app);

// Function to verify group access
export const verifyGroupAccess = async (groupId: string, password: string) => {
  try {
    // Sign in anonymously if not already signed in
    if (!auth.currentUser) {
      await signInAnonymously(auth);
    }

    // Verify group exists and password matches
    const groupRef = doc(firestore, 'groups', groupId);
    const groupDoc = await getDoc(groupRef);
    
    if (groupDoc.exists() && groupDoc.data().qrCode === password) {
      // Store groupId and qrCode in AsyncStorage for future use
      await AsyncStorage.setItem('currentGroupId', groupId);
      await AsyncStorage.setItem('currentQrCode', password);
      await AsyncStorage.setItem('isLoggedIn', 'true');
      return true;
    }
    return false;
  } catch (error) {
    console.error('Error verifying group access:', error);
    return false;
  }
};

// Function to get current group ID
export const getCurrentGroupId = async () => {
  return await AsyncStorage.getItem('currentGroupId');
};

// Function to get current QR code
export const getCurrentQrCode = async () => {
  return await AsyncStorage.getItem('currentQrCode');
};

// Function to check if user is logged in
export const isLoggedIn = async () => {
  return await AsyncStorage.getItem('isLoggedIn') === 'true';
};

// Function to logout
export const logout = async () => {
  try {
    await AsyncStorage.removeItem('currentGroupId');
    await AsyncStorage.removeItem('currentQrCode');
    await AsyncStorage.removeItem('isLoggedIn');
    await auth.signOut();
    return true;
  } catch (error) {
    console.error('Error logging out:', error);
    return false;
  }
};

// Function to create a report
export const createReport = async (reportData: any) => {
  try {
    const groupId = getCurrentGroupId();
    if (!groupId) {
      throw new Error('No group ID found. Please log in first.');
    }

    // Add report to Firestore
    const reportsRef = collection(firestore, 'reports');
    await addDoc(reportsRef, {
      ...reportData,
      groupId,
      createdAt: serverTimestamp()
    });

    return true;
  } catch (error) {
    console.error('Error creating report:', error);
    return false;
  }
};

// Function to restore anonymous auth session
export const restoreAuthSession = async () => {
  try {
    const isLoggedIn = await AsyncStorage.getItem('isLoggedIn') === 'true';
    const groupId = await AsyncStorage.getItem('currentGroupId');
    const qrCode = await AsyncStorage.getItem('currentQrCode');

    if (isLoggedIn && groupId && qrCode) {
      // Sign in anonymously if not already signed in
      if (!auth.currentUser) {
        await signInAnonymously(auth);
      }

      // Verify group still exists and password still matches
      const groupRef = doc(firestore, 'groups', groupId);
      const groupDoc = await getDoc(groupRef);
      
      if (groupDoc.exists() && groupDoc.data().qrCode === qrCode) {
        return true;
      }
    }
    
    // If any check fails, clear stored data
    await AsyncStorage.multiRemove(['currentGroupId', 'currentQrCode', 'isLoggedIn']);
    return false;
  } catch (error) {
    console.error('Error restoring auth session:', error);
    return false;
  }
};

export { 
  app, 
  auth, 
  firestore, 
  storage,
  collection,
  query,
  where,
  getDocs,
  orderBy,
  onSnapshot,
  addDoc,
  serverTimestamp,
  Timestamp,
  doc,
  getDoc
}; 